import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from 'antd';
import { getCurrentShiftEmployees } from '../services/breakService';
import websocketService from '../services/websocket';
import { debounce } from '../utils/debounce';
import { apiCache } from '../utils/apiCache';

const EmployeeStatusInline = () => {
  const [boxData, setBoxData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const currentUserId = parseInt(sessionStorage.getItem('id'));
  const userRole = sessionStorage.getItem('role');

  // ดึงข้อมูลพนักงาน
  const fetchEmployees = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const response = await getCurrentShiftEmployees(currentUserId, userRole);
      
      if (response.success && Array.isArray(response.result)) {
        // API ส่ง result เป็น array ของ { name, online, break, employees }
        setBoxData(response.result);
        // เก็บข้อมูลพนักงานสำหรับ tooltip
        if (response.employees) {
          setEmployees(response.employees);
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, userRole]);

  // Debounced version of fetchEmployees
  const debouncedFetchEmployees = useCallback(
    debounce(fetchEmployees, 2000),
    [fetchEmployees]
  );

  useEffect(() => {
    fetchEmployees();
    
    if (!websocketService.getConnectionStatus().isConnected) {
      websocketService.connect();
    }
    
    const handleBreakEvents = (eventData) => {
      apiCache.clear();
      debouncedFetchEmployees();
    };

    const handleWorkEvents = (eventData) => {
      apiCache.clear();
      debouncedFetchEmployees();
    };

    websocketService.onBreakEvent(handleBreakEvents);
    websocketService.onWorkEvent(handleWorkEvents);

    websocketService.onUserEvent((eventData) => {
      apiCache.clear();
      debouncedFetchEmployees();
    });

    const pollingInterval = setInterval(() => {
      if (!websocketService.getConnectionStatus().isConnected) {
        fetchEmployees();
      }
    }, 120000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [currentUserId, debouncedFetchEmployees, fetchEmployees]);

  // สีสำหรับสถานะต่างๆ
  const statusColors = {
    Ready: '#10b981',      // เขียวสดใส
    Break: '#3b82f6',      // น้ำเงินสดใส
    LunchBreak: '#f59e0b', // ส้มสดใส
    Dayoff: '#9ca3af'      // เทาอ่อน
  };

  // สร้าง tooltip content
  const createTooltip = (boxName, boxEmployees) => {
    if (!boxEmployees || boxEmployees.length === 0) {
      return (
        <div className="text-left">
          {/* <div className="font-bold mb-2">{boxName}</div> */}
          <div className="text-gray-500 text-sm">ไม่มีพนักงานในกะวันนี้</div> 
        </div>
      );
    }

    const onlineEmployees = boxEmployees.filter(emp =>
      emp.id !== currentUserId && emp.status === 'Ready'
    );
    // ออฟไลน์ = ทุกคนที่ไม่ออนไลน์ (ไม่มี "พัก" แล้ว เพราะถอดปุ่มพักไปแล้ว)
    const offlineEmployees = boxEmployees.filter(emp =>
      emp.id !== currentUserId && emp.status !== 'Ready'
    );

    return (
      <div className="text-left">
        {/* <div className="font-bold mb-2">{boxName}</div> */}
        <div className="mb-2">
          <span className="font-medium">ออนไลน์ ({onlineEmployees.length}):</span>
          {onlineEmployees.length > 0 ? (
            <div className="ml-2 mt-1">
              {onlineEmployees.map(emp => (
                <div key={emp.id} className="flex items-center space-x-2 text-sm">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: statusColors[emp.status] || statusColors.Dayoff }}
                  ></div>
                  <span>{emp.UserName}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="ml-2 text-gray-500 text-sm">ไม่มีคนออนไลน์</div>
          )}
        </div>
        <div className="mb-2">
          <span className="font-medium">ออฟไลน์ ({offlineEmployees.length}):</span>
          {offlineEmployees.length > 0 ? (
            <div className="ml-2 mt-1">
              {offlineEmployees.map(emp => (
                <div key={emp.id} className="flex items-center space-x-2 text-sm">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: statusColors[emp.status] || statusColors.Dayoff }}
                  ></div>
                  <span>{emp.UserName}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="ml-2 text-gray-500 text-sm">ไม่มีคนออฟไลน์</div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-3 ml-4">
        <div className="animate-pulse flex space-x-3">
          <div className="w-28 h-12 bg-gray-200 rounded border"></div>
          <div className="w-28 h-12 bg-gray-200 rounded border"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3 ml-4">
      {/* วนแสดงกล่องตามข้อมูลที่ API ส่งมา */}
      {boxData.map((box) => {
        // ออฟไลน์ = คนในกล่องที่ไม่ออนไลน์ (ไม่มี "พัก" แล้ว)
        const offlineCount = (box.employees || []).filter(e => e.status !== 'Ready').length;
        return (
          <Tooltip
            key={box.name}
            title={createTooltip(box.name, box.employees)}
            placement="bottom"
            style={{ maxWidth: '300px' }}
          >
            <div className="relative w-28 h-12 bg-white border-2 border-gray-300 rounded-lg flex items-center justify-center shadow-sm cursor-pointer hover:border-blue-400 transition-colors">
              {/* ตัวเลขซ้าย - คนออนไลน์ */}
              <div className="absolute top-1 left-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {box.online}
              </div>

              {/* ตัวเลขขวา - คนออฟไลน์ */}
              <div className="absolute top-1 right-1 w-5 h-5 bg-gray-400 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {offlineCount}
              </div>

              {/* ข้อความกลาง */}
              <span className="text-sm font-bold text-gray-800">
                {box.name}
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default EmployeeStatusInline; 